import { Container, Graphics, Sprite, Text, TextStyle } from 'pixi.js';
import { ARENA_TOP, SCENE_H, SCENE_W, type GameConfig, type GiftInfo } from '@rekrut/shared';
import { WEAPON_META } from '../sim/weapons.js';
import type { TextCache } from './text.js';
import type { Tex } from './textures.js';

const PANEL_W = 232;
const PANEL_X = SCENE_W - PANEL_W - 20;
const PANEL_Y = ARENA_TOP + 16;
const ROW_H = 80;
const HEAD_H = 40;

/** Колонки строки: подарок, знак равенства, оружие. */
const COL_MUL = 18;
const COL_GIFT = 56;
const COL_EQ = 110;
const COL_ART = 164;

/** Оружие — такая же квадратная плитка, как подарок: две равные иконки в строке. */
const ART_SIZE = 66;
/**
 * Подарок нарисован крупно: в 46 пикселях роза от пончика не отличается,
 * а именно по картинке зритель ищет подарок в витрине.
 */
const GIFT_SIZE = 68;

/**
 * Прайс оружия — полупрозрачная плашка у правого края арены.
 *
 * Строка читается как формула: «×1 подарок = вот это оружие». Ни цен, ни
 * названий: и то и другое зритель всё равно сверяет по картинке подарка в
 * своей витрине, а лишний текст в оверлее не читают.
 *
 * Подарок и оружие — две одинаковые квадратные плитки, на каждой бейдж по
 * нижнему краю с нахлёстом: «×1» у подарка, «×5» и «×12» у ракет. Счётчик
 * отдельной колонкой съедал бы ширину, которой в вертикальном формате нет.
 */
export class PricePanel {
  readonly view = new Container();

  private readonly bg = new Graphics();
  /**
   * Подложки бейджей — отдельным слоем ПОВЕРХ плиток.
   *
   * Раньше пилюли рисовались в фоне, а тексты добавлялись в контейнер раньше
   * картинок, — и то и другое уезжало под плитку. Порядок добавления в Pixi и
   * есть порядок отрисовки, поэтому слои разведены явно, а не «как получилось».
   */
  private readonly fg = new Graphics();
  private readonly title: Text;
  private readonly rows: {
    mul: Text;
    icon: Sprite;
    eq: Text;
    count: Text;
    art: Sprite;
  }[] = [];
  private catalog: GiftInfo[] = [];
  private last = new Map<string, string>();

  constructor(
    private readonly cfg: GameConfig,
    private readonly texts: TextCache,
    private readonly tex: Tex,
  ) {
    const mk = (size: number, weight: string, color: number): Text =>
      new Text({
        text: '',
        style: new TextStyle({
          fontFamily: 'system-ui, "Noto Color Emoji", sans-serif',
          fontSize: size,
          fontWeight: weight as never,
          fill: color,
          stroke: { color: 0x05070c, width: Math.max(2, size * 0.1) },
        }),
      });

    this.title = mk(17, '800', 0x8fa0bb);
    this.title.text = 'ОТПРАВЬ ПОДАРОК';
    this.title.position.set(PANEL_X + COL_MUL, PANEL_Y + 12);

    for (let i = 0; i < cfg.weapons.tiers.length; i++) {
      const cy = PANEL_Y + HEAD_H + i * ROW_H + ROW_H / 2;

      // Множитель — бейджем внизу самого подарка, с нахлёстом: так он
      // читается как подпись к картинке, а не как отдельная колонка.
      const mul = mk(18, '900', 0xffffff);
      mul.anchor.set(0.5, 0.5);
      mul.text = '×1';

      const icon = new Sprite();
      icon.anchor.set(0.5);
      icon.position.set(PANEL_X + COL_GIFT, cy);

      const eq = mk(24, '800', 0x5d6b83);
      eq.anchor.set(0.5, 0.5);
      eq.text = '=';
      eq.position.set(PANEL_X + COL_EQ, cy);

      const count = mk(18, '900', 0xffffff);
      count.anchor.set(0.5, 0.5);

      const art = new Sprite();
      art.anchor.set(0.5);

      this.rows.push({ mul, icon, eq, count, art });
    }

    // Слои снизу вверх: фон плашки → плитки → подложки бейджей → надписи.
    this.view.addChild(this.bg);
    for (const r of this.rows) this.view.addChild(r.icon, r.art, r.eq);
    this.view.addChild(this.fg);
    for (const r of this.rows) this.view.addChild(r.mul, r.count);
    this.view.addChild(this.title);
  }

  setCatalog(c: GiftInfo[]): void {
    this.catalog = c;
    this.last.clear();
  }

  /**
   * Альбомный режим: плашка держится правого верхнего угла широкого кадра.
   * Внутренняя вёрстка не меняется — сдвигается контейнер целиком.
   */
  setLandscape(on: boolean): void {
    this.view.position.set(on ? SCENE_H - SCENE_W : 0, on ? -64 : 0);
  }

  update(): void {
    const tiers = this.cfg.weapons.tiers;
    const h = HEAD_H + tiers.length * ROW_H + 8;

    const g = this.bg;
    g.clear();
    this.fg.clear();
    g.roundRect(PANEL_X, PANEL_Y, PANEL_W, h, 16).fill({ color: 0x070a12, alpha: 0.68 });
    g.roundRect(PANEL_X, PANEL_Y, PANEL_W, h, 16).stroke({ color: 0x2a3448, width: 2, alpha: 0.8 });

    for (let i = 0; i < this.rows.length; i++) {
      const tier = tiers[i]!;
      const row = this.rows[i]!;
      const meta = WEAPON_META[tier.weapon];
      const cy = PANEL_Y + HEAD_H + i * ROW_H + ROW_H / 2;

      if (i < this.rows.length - 1) {
        const ly = PANEL_Y + HEAD_H + (i + 1) * ROW_H;
        g.moveTo(PANEL_X + 18, ly)
          .lineTo(PANEL_X + PANEL_W - 18, ly)
          .stroke({ color: 0x1e2636, width: 1 });
      }

      const gift = this.cheapestFor(tier.min, tier.max);

      // Значок подарка: заглушка из конфига, пока не пришёл каталог комнаты.
      const wanted = gift?.iconUrl || tier.icon;
      if (row.icon.label !== wanted) {
        row.icon.label = wanted;
        row.icon.texture = this.texts.get(tier.icon, 36).texture;
        if (gift?.iconUrl) loadIcon(gift.iconUrl, row.icon);
      }
      row.icon.width = GIFT_SIZE;
      row.icon.height = GIFT_SIZE;
      row.eq.position.set(PANEL_X + COL_EQ, cy);

      // Бейдж сидит НА картинке, по её нижнему краю, с нахлёстом.
      const bx = PANEL_X + COL_GIFT;
      const by = cy + GIFT_SIZE / 2 - 8;
      this.fg.roundRect(bx - 24, by - 13, 48, 26, 13).fill({ color: 0x0b1120, alpha: 0.95 });
      this.fg.roundRect(bx - 24, by - 13, 48, 26, 13).stroke({ color: 0x3a465e, width: 2 });
      row.mul.position.set(bx, by);

      const art =
        tier.mark === 'rifle'
          ? this.tex.wRifle
          : tier.mark === 'laser'
            ? this.tex.wLaser
            : this.tex.wRocket;
      if (row.art.texture !== art) row.art.texture = art;
      row.art.rotation = 0;
      row.art.width = ART_SIZE;
      row.art.height = ART_SIZE;

      row.art.position.set(PANEL_X + COL_ART, cy);
      // Счётчик ставится бейджем на самой плитке — ровно как «×1» у подарка.
      // Отдельная колонка под «5×» съедала бы ширину, которой нет.
      const count = tier.label ? `×${tier.label}` : '';
      this.set(row.count, `c${i}`, count);
      row.count.visible = count !== '';
      if (count) {
        const ax = PANEL_X + COL_ART;
        const ay = cy + ART_SIZE / 2 - 8;
        this.fg.roundRect(ax - 26, ay - 13, 52, 26, 13).fill({ color: 0x0b1120, alpha: 0.95 });
        this.fg.roundRect(ax - 26, ay - 13, 52, 26, 13).stroke({ color: 0x3a465e, width: 2 });
        row.count.position.set(ax, ay);
      }
    }
  }

  private cheapestFor(min: number, max: number | null): GiftInfo | null {
    let best: GiftInfo | null = null;
    for (const g of this.catalog) {
      if (g.diamonds < min) continue;
      if (max !== null && g.diamonds > max) continue;
      if (!best || g.diamonds < best.diamonds) best = g;
    }
    return best;
  }

  private set(node: Text, key: string, value: string): void {
    if (this.last.get(key) === value) return;
    this.last.set(key, value);
    node.text = value;
  }
}

/**
 * Картинка подарка с CDN TikTok. Грузится необязательно: при 403 или
 * отсутствии CORS остаётся значок-заглушка, и плашка всё равно читается.
 */
function loadIcon(url: string, sprite: Sprite): void {
  const img = new Image();
  img.crossOrigin = 'anonymous';
  img.onload = () => {
    const c = document.createElement('canvas');
    c.width = 72;
    c.height = 72;
    c.getContext('2d')!.drawImage(img, 0, 0, 72, 72);
    sprite.texture = Sprite.from(c).texture;
  };
  img.src = url;
}
